/* eslint-disable react/jsx-no-useless-fragment */
import React from 'react';
import { Button, Dialog } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Typography } from '@mui/material';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import style from './confirmation.module.scss';
import {
    ThemeProvider,
    Theme,
    StyledEngineProvider,
} from '@mui/material/styles';

type IButtonColor =
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'info'
    | 'warning';

interface ConfirmationProps {
    visible: boolean;
    title: string;
    subtitle?: string;
    children: React.ReactNode;

    actions?: IActions | null | undefined;
    onClose: () => void;

    notCloseOnBackgroundClick?: boolean;
    fullWidth?: boolean;
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
}

export interface IButtonProps {
    onClick?(): void | Promise<void>;
    content?: string;
    color?: IButtonColor;
}

export interface IActions {
    confirm?: IButtonProps | null;
    cancel?: IButtonProps | null;
    delete?: IButtonProps | null;
    hideDeleteDivider?: boolean;
}

export const Confirmation: React.FC<ConfirmationProps> = (
    props: ConfirmationProps
) => {
    const theme = useTheme();
    const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const confirmationTheme = {
        ...theme,
        overrides: {
            MuiDialogTitle: {
                root: {
                    padding: isPhone ? '16px 24px' : '40px 43px 0 43px',
                },
            },
            MuiDialogContent: {
                root: {
                    padding: isPhone ? '8px 24px' : '4px 43px 0px 43px',
                },
            },
            MuiDialogActions: {
                root: {
                    padding: isPhone ? '24px 24px' : '10px 43px 40px 43px',
                },
            },
        },
    };

    const Title = (
        <Typography id="scroll-dialog-title-id" variant={'h6'}>
            {props.title}
        </Typography>
    );

    const Subtitle = props.subtitle ? (
        <Typography variant={'body1'}>{props.subtitle}</Typography>
    ) : null;

    let Actions: JSX.Element | null = null;

    if (props.actions) {
        let CancelButton: JSX.Element | null = null;
        if (props.actions.cancel) {
            CancelButton = (
                <Button
                    onClick={props.onClose}
                    variant="contained"
                    color="secondary"
                    disableElevation
                >
                    {props.actions.cancel.content
                        ? props.actions.cancel.content
                        : 'Cancel'}
                </Button>
            );
        }

        let DeleteButton: JSX.Element | null = null;
        if (props.actions.delete) {
            DeleteButton = (
                <Button color="error" variant="contained">
                    Delete
                </Button>
            );
        }

        const confirmProps = props.actions.confirm;
        const ConfirmButton = confirmProps ? (
            <Button
                onClick = {confirmProps.onClick}
                color={confirmProps.color ? confirmProps.color : 'primary'}
                variant="contained"
            >
                {confirmProps.content ? confirmProps.content : 'Confirm'}
            </Button>
        ) : null;

        Actions = (
            <DialogActions>
                <Box
                    flexGrow={1}
                    className={style['actions-content']}
                    display="flex"
                    justifyContent="space-between"
                    flexDirection={isTablet ? 'column-reverse' : 'row'}
                >
                    <Box
                        display="flex"
                        flexDirection={isTablet ? 'column-reverse' : 'row'}
                    >
                        {DeleteButton}
                        {props.actions.delete != null &&
                            !props.actions.hideDeleteDivider &&
                            isTablet && (
                                <Box className={style['border-line']}></Box>
                            )}
                    </Box>
                    <Box
                        display="flex"
                        flexDirection={isTablet ? 'column-reverse' : 'row'}
                    >
                        {CancelButton}
                        {ConfirmButton}
                    </Box>
                </Box>
            </DialogActions>
        );
    }

    const onCloseDialog = (
        event: any,
        reason: 'backdropClick' | 'escapeKeyDown'
    ) => {
        if (
            props.notCloseOnBackgroundClick !== true ||
            reason !== 'backdropClick'
        ) {
            props.onClose();
        }
    };

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={confirmationTheme}>
                <Dialog
                    open={props.visible}
                    onClose={onCloseDialog}
                    aria-labelledby="scroll-dialog-title-id"
                    className={style['confirm-modal']}
                    scroll={isTablet ? 'body' : 'paper'}
                    fullWidth={props.fullWidth}
                    maxWidth={props.maxWidth}
                >
                    <DialogTitle>{Title}</DialogTitle>

                    <DialogContent>
                        <Box mb={2}>
                            {Subtitle}
                            {props.children}
                        </Box>
                    </DialogContent>
                    {Actions}
                </Dialog>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};
